import React, { useState, useEffect, useRef } from 'react';
import { SpecialEvent, SubscriptionPlan, FlashBoost, VenueIdentity } from '../types';
import { SpecialCard } from './SpecialCard';
import { supabase } from "../services/supabaseClient";

interface MerchantPortalProps {
  events: SpecialEvent[];
  identity: VenueIdentity;
  venueId?: string | null;
  onUpdateIdentity: (newIdentity: VenueIdentity) => void;
  onRemoveEvent: (id: string) => void;
  onEditEvent: (event: SpecialEvent) => void;
  onAddNew: () => void;
  plan: SubscriptionPlan;
  initialTab?: 'specials' | 'identity' | 'flash';
  subscriptionStatus?: string | null;
  listingStatus?: string | null;
}

const CropModal: React.FC<{
  image: string;
  aspect: number;
  onSave: (croppedImage: string) => void;
  onCancel: () => void;
}> = ({ image, aspect, onSave, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);

  // Constants for the visual crop box
  const BOX_WIDTH = 320;
  const BOX_HEIGHT = 320 / aspect;

  const handleApply = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Desired output resolution
    const outWidth = aspect === 1 ? 1024 : 1920;
    const outHeight = outWidth / aspect;
    canvas.width = outWidth;
    canvas.height = outHeight;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, outWidth, outHeight);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Multiplier to convert from visual BOX coordinates to high-res output coordinates
    const m = outWidth / BOX_WIDTH;

    // The image's displayed size in the BOX coordinate system
    const displayW = BOX_WIDTH * zoom;
    const displayH = (BOX_WIDTH * (img.naturalHeight / img.naturalWidth)) * zoom;

    // The center position in the BOX coordinate system
    const centerX = BOX_WIDTH / 2 + offset.x;
    const centerY = BOX_HEIGHT / 2 + offset.y;

    ctx.drawImage(
      img,
      (centerX - displayW / 2) * m,
      (centerY - displayH / 2) * m,
      displayW * m,
      displayH * m
    );

    onSave(canvas.toDataURL('image/jpeg', 0.9));
  };

  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    setLastPos({ x: clientX, y: clientY });
  };

  const handleDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const dx = clientX - lastPos.x;
    const dy = clientY - lastPos.y;
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    setLastPos({ x: clientX, y: clientY });
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md">
      <div className="bg-white p-8 md:p-12 rounded-[3.5rem] shadow-2xl border border-black/[0.03] grainy max-w-lg w-full text-center space-y-8 animate-in zoom-in-95 duration-300">
        <h3 className="text-2xl font-black serif tracking-tight">Adjust <span className="text-amber-600">Crop.</span></h3>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Drag to reposition, slider to scale</p>

        <div
          className="relative mx-auto overflow-hidden bg-gray-100 border-2 border-black/5 rounded-2xl cursor-move touch-none select-none"
          style={{ width: `${BOX_WIDTH}px`, height: `${BOX_HEIGHT}px` }}
          onMouseDown={startDrag}
          onMouseMove={handleDrag}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
          onTouchStart={startDrag}
          onTouchMove={handleDrag}
          onTouchEnd={() => setIsDragging(false)}
        >
          <img
            ref={imgRef}
            src={image}
            alt="Crop Preview"
            onLoad={() => setImgLoaded(true)}
            className="absolute pointer-events-none max-w-none origin-center"
            style={{
              width: `${zoom * 100}%`,
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
          <div className="absolute inset-0 border border-white/20 pointer-events-none" />
        </div>

        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <span className="text-[10px] font-black text-gray-400">−</span>
            <input
              type="range" min="0.5" max="4" step="0.01"
              value={zoom} onChange={e => setZoom(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-amber-500"
            />
            <span className="text-[10px] font-black text-gray-400">+</span>
          </div>
          <div className="flex gap-3">
            <button onClick={onCancel} className="flex-1 py-4 bg-gray-50 text-gray-400 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-black/5 hover:bg-gray-100 transition-all">Cancel</button>
            <button onClick={handleApply} className="flex-1 py-4 bg-black text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all shadow-lg active:scale-95">Set Photo</button>
          </div>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

const MerchantPortal: React.FC<MerchantPortalProps> = ({
  events,
  identity,
  venueId,
  onUpdateIdentity,
  onRemoveEvent,
  onEditEvent,
  onAddNew,
  plan,
  initialTab = 'specials',
  subscriptionStatus,
  listingStatus,
}) => {
  const [activeTab, setActiveTab] = useState<'specials' | 'identity' | 'flash'>(initialTab);
  const [isBoosting, setIsBoosting] = useState(false);
  const [boostDuration, setBoostDuration] = useState(2);
  const [boostMessage, setBoostMessage] = useState("Flash Deal: Half off all craft pours for the next 2 hours! 🍺");
  const [activeBoost, setActiveBoost] = useState<FlashBoost | null>(null);
  const [boostError, setBoostError] = useState<string | null>(null);
  const [boostResult, setBoostResult] = useState<string | null>(null);
  
  const [localIdentity, setLocalIdentity] = useState<VenueIdentity>(identity);
  const [isSaving, setIsSaving] = useState(false);
  const [isIdentityDraftDirty, setIsIdentityDraftDirty] = useState(false)
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [genLoading, setGenLoading] = useState<string | null>(null);

  const [cropData, setCropData] = useState<{ image: string, aspect: number, field: string, index?: number } | null>(null);

  useEffect(() => {
    if (!isIdentityDraftDirty) {
      setLocalIdentity(identity);
    }
  }, [identity, isIdentityDraftDirty]);
  
  const isProfileComplete =
    !!identity.venueName?.trim() &&
    !!identity.streetAddress?.trim() &&
    !!identity.city?.trim() &&
    !!identity.state?.trim() &&
    !!identity.zipCode?.trim();

  const normalizedSubscriptionStatus = (subscriptionStatus || "").trim().toLowerCase();
  const normalizedListingStatus = (listingStatus || "").trim().toLowerCase();
  const hasMembershipAccess = ["active", "trialing"].includes(normalizedSubscriptionStatus) || normalizedListingStatus === "active";

  const updateIdentityField = <K extends keyof VenueIdentity>(field: K, value: VenueIdentity[K]) => {
    setIsIdentityDraftDirty(true);
    setLocalIdentity((prev) => ({ ...prev, [field]: value }));
  };
  
  const handleManageBilling = async () => {
    try {
      setIsOpeningBillingPortal(true);
      setBillingError(null);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error("No active session. Please log in again.");

      const res = await fetch('/.netlify/functions/create-billing-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          venue_id: venueId,
          return_url: '/#/venue-hub?tab=identity',
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to open billing portal');
      if (!data?.url) throw new Error('No billing portal URL returned');

      window.location.href = data.url;
    } catch (err: any) {
      setBillingError(err?.message || 'Unable to open billing portal right now.');
    } finally {
      setIsOpeningBillingPortal(false);
    }
  };

  useEffect(() => {
  // If parent changes initialTab or we arrive via deep link, honor it.
  setActiveTab(initialTab);
}, [initialTab]);

  /**
   * Starts Stripe Checkout by calling your Netlify function:
   * /.netlify/functions/create-checkout-session
   *
   * IMPORTANT:
   * - Your function requires venueId + userId.
   * - In your current UI model, those might not live inside VenueIdentity yet.
   * - This code tries a few common places to pull them from.
   * - If they are missing, it will STOP and show a clear error.
   */
  const startStripeCheckout = async (updatedIdentity: VenueIdentity) => {
    // Try to pull IDs from anywhere you may already be storing them
    const venueId =
      (updatedIdentity as any)?.venueId ||
      (updatedIdentity as any)?.id ||
      (updatedIdentity as any)?.venue_id ||
      (updatedIdentity as any)?.venue?.id;

    const userId =
      (updatedIdentity as any)?.userId ||
      (updatedIdentity as any)?.ownerId ||
      (updatedIdentity as any)?.user_id ||
      (updatedIdentity as any)?.owner_id ||
      (updatedIdentity as any)?.user?.id;

    const email =
      (updatedIdentity as any)?.email ||
      (updatedIdentity as any)?.ownerEmail ||
      '';

    // Your Netlify function hard-requires these
    if (!venueId || !userId) {
      throw new Error(
        `Missing required IDs for checkout. venueId=${String(venueId)} userId=${String(userId)}`
      );
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    const checkoutHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) {
      checkoutHeaders.Authorization = `Bearer ${accessToken}`;
    }

    const res = await fetch('/.netlify/functions/create-checkout-session', {
      method: 'POST',
      headers: checkoutHeaders,
      body: JSON.stringify({
        venueId,
        userId,
        email,
      }),
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(msg || 'Failed to create checkout session');
    }

    const data = await res.json();
    if (!data?.url) throw new Error('No checkout URL returned');

    window.location.href = data.url;
  };
  
  async function saveVenueProfileToSupabase(identity: any) {
    // Get the logged-in user (so we have owner_user_id + email)
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr) throw userErr;
    
    const user = userData?.user;
    if (!user?.id) throw new Error("Not logged in (missing user id).");

     const normalizedEmail = (user.email || "").toLowerCase();

    // Resolve venue id from all available sources (identity -> prop -> localStorage -> backend lookup)
    let resolvedVenueId =
      identity.id ||
      venueId ||
      localStorage.getItem('pendingVenueId') ||
      null;

    if (!resolvedVenueId && normalizedEmail) {
      const profileRes = await fetch(`/.netlify/functions/get-venue-profile?business_email=${encodeURIComponent(normalizedEmail)}`);
      const profilePayload = await profileRes.json().catch(() => ({}));
      const lookedUpId = profilePayload?.data?.venue?.id;
      if (lookedUpId) {
        resolvedVenueId = lookedUpId;
        localStorage.setItem('pendingVenueId', String(lookedUpId));
      }
    }

    if (!resolvedVenueId) {
      throw new Error('Missing venueId. Could not resolve your venue row yet. Please refresh and try again.');
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    const saveHeaders: Record<string, string> = { "Content-Type": "application/json" };
    if (accessToken) {
      saveHeaders.Authorization = `Bearer ${accessToken}`;
    }
    
    const res = await fetch("/.netlify/functions/save-venue-profile", {
      method: "POST",
      headers: saveHeaders,
      body: JSON.stringify({
        // IMPORTANT: function expects venue_id (not venueId)
        venue_id: resolvedVenueId,
        owner_user_id: user.id,
        business_email: normalizedEmail,
  
        venue_name: identity.venueName,
        address: identity.address,
        street_address: identity.streetAddress,
        city: identity.city,
        state: identity.state,
        zip_code: identity.zipCode,
        website: identity.website,
        facebook: identity.facebook,
        instagram: identity.instagram,
        x_handle: identity.xHandle,
        logo_url: identity.logoUrl || null,
        main_image_url: identity.mainImageUrl || '/images/Auto_Main_Image.jpg',
        gallery_images: Array.isArray(identity.galleryImages) ? identity.galleryImages.slice(0, 4) : []
      }),
    });
    
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(payload?.error || "Failed to save venue profile.");
  
    return { ...payload, resolvedVenueId };
  }

  const handleIdentitySave = async (Leeds: React.FormEvent) => {
    Leeds.preventDefault();
    setIsSaving(true);
  
    let updatedIdentity: VenueIdentity = { ...localIdentity };
  
    // 1) If main image is blank, use default stock image
    const DEFAULT_MAIN_IMAGE_URL = '/images/Auto_Main_Image.jpg';
  
    if (!updatedIdentity.mainImageUrl || updatedIdentity.mainImageUrl.trim() === '') {
      updatedIdentity.mainImageUrl = DEFAULT_MAIN_IMAGE_URL;
      setLocalIdentity(updatedIdentity);
    }
  
    try {
      // Build combined address string (optional but useful)
      updatedIdentity.address =
        `${updatedIdentity.streetAddress || ''}, ${updatedIdentity.city || ''}, ${updatedIdentity.state || ''} ${updatedIdentity.zipCode || ''}`
          .replace(/\s+/g, ' ')
          .replace(/,\s*,/g, ',')
          .trim();
  
      /// ✅ 2a) Save to Supabase first (resolve venue id robustly inside this function)
      const saveResult = await saveVenueProfileToSupabase(updatedIdentity);
      (updatedIdentity as any).id = (saveResult as any)?.resolvedVenueId || (updatedIdentity as any).id;
  
      // ✅ 2b) Update parent state so UI reflects latest immediately
      onUpdateIdentity(updatedIdentity);
      setIsIdentityDraftDirty(false);
  
      // ✅ Redirect to "My Specials" after saving profile
      setActiveTab('specials');
      window.location.hash = '#/venue-hub?tab=specials';
      return;
  
    } catch (err: any) {
      console.error(err);
      alert(err?.message || 'Could not save profile. Please try again.');
      setActiveTab('identity');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'logoUrl' | 'mainImageUrl' | 'gallery',
    index?: number
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      const aspect = field === 'logoUrl' ? 1 : field === 'mainImageUrl' ? 16 / 9 : 1;
      setCropData({ image: data, aspect, field, index });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onCropComplete = (croppedImage: string) => {
    if (!cropData) return;
    setIsIdentityDraftDirty(true);
    const { field, index } = cropData;
    if (field === 'gallery') {
      const newGallery = [...localIdentity.galleryImages];
      if (typeof index === 'number') {
        newGallery[index] = croppedImage;
      } else if (newGallery.length < 4) {
        newGallery.push(croppedImage);
      }
      setLocalIdentity(prev => ({ ...prev, galleryImages: newGallery.slice(0, 4) }));
    } else {
      setLocalIdentity(prev => ({ ...prev, [field]: croppedImage } as any));
    }
    setCropData(null);
  };

  const handleRemoveGalleryImage = (idx: number) => {
    setIsIdentityDraftDirty(true);
    const newGallery = localIdentity.galleryImages.filter((_, i) => i !== idx);
    setLocalIdentity(prev => ({ ...prev, galleryImages: newGallery }));
  };

  const handleLaunchBoost = async () => {
    try {
      setIsBoosting(true);
      setBoostError(null);
      setBoostResult(null);

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error('No active session. Please log in again.');

      const res = await fetch('/.netlify/functions/launch-flash-boost', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          venue_id: venueId,
          message: boostMessage,
          duration_hours: boostDuration,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not launch Flash Boost.');
      
      setActiveBoost({
        id: Date.now().toString(),
        message: boostMessage,
        timestamp: new Date().toISOString(),
        durationHours: boostDuration,
        active: true,
        reachCount: data?.sent ?? data?.subscribers ?? 0,
      });
      const sent = Number(data?.sent || 0);
      const subscribers = Number(data?.subscribers || 0);
      const failed = Number(data?.failed || 0);
      const warning = String(data?.warning || '').trim();

      if (warning) {
        setBoostResult(`Flash Boost launched for ${subscribers} subscribers.`);
        setBoostError(warning);
      } else if (failed > 0) {
        setBoostError(`Flash Boost launched, but ${failed} of ${subscribers} deliveries failed.`);
      } else {
        setBoostResult(`Flash Boost launched: ${sent}/${subscribers} emails sent.`);
      }
    } catch (err: any) {
      setBoostError(err?.message || 'Unable to launch Flash Boost right now.');
    } finally {
      setIsBoosting(false);
    }
  };
  
  // ✅ Gate: venue profile must be complete before using Specials
  const profileComplete =
    !!identity?.venueName &&
    !!(identity?.streetAddress || identity?.address) &&
    !!identity?.city &&
    !!identity?.state &&
    !!identity?.zipCode;

  return (
    <div className="max-w-7xl mx-auto px-6 py-40 min-h-screen">
      {cropData && (
        <CropModal
          image={cropData.image}
          aspect={cropData.aspect}
          onCancel={() => setCropData(null)}
          onSave={onCropComplete}
        />
      )}

      <div className="bg-[#0a0a0a] rounded-[4rem] p-12 md:p-16 mb-20 text-white relative overflow-hidden shadow-2xl grainy ring-4 ring-amber-500/20 shadow-amber-900/40">
        <div className="absolute top-0 right-0 p-12 opacity-5 scale-150 rotate-12">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-64 w-64" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <div className="relative z-10">
          <div className="flex flex-wrap items-center gap-6 mb-8">
            <span className="px-5 py-2 font-black text-[10px] tracking-[0.3em] uppercase rounded-full bg-amber-500 text-black shadow-lg shadow-amber-500/40">Verified Partner</span>
                        <span className={`font-black text-[10px] uppercase tracking-widest ${hasMembershipAccess ? 'text-amber-500 animate-pulse' : 'text-red-300'}`}>
              ● MEMBERSHIP {hasMembershipAccess ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
          <h2 className="text-5xl md:text-8xl font-black mb-6 serif tracking-tighter leading-none">
            Venue <span className="italic text-amber-500">Hub.</span>
          </h2>
          <div className="flex flex-wrap gap-4 items-center mt-12">
            <button
              onClick={() => profileComplete && setActiveTab('specials')}
              className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all ${
                activeTab === 'specials'
                  ? 'bg-white text-black shadow-xl'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              } ${!profileComplete ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              My Specials
            </button>

            <button
              onClick={() => setActiveTab('identity')}
              className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all border-2 ${activeTab === 'identity' ? 'bg-white text-black border-white shadow-xl' : 'bg-black/40 text-white border-amber-500/40 hover:border-amber-500 hover:bg-black/60'}`}
            >
              Venue Profile
            </button>
            <button
              onClick={() => hasMembershipAccess && setActiveTab('flash')}
              className={`px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all border-2 ${activeTab === 'flash' ? 'bg-amber-500 text-black border-amber-500 shadow-xl' : 'bg-black/40 text-white border-amber-500/40 hover:border-amber-500 hover:bg-black/60'}`}
              disabled={!hasMembershipAccess}
              >
              Flash Boost
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'specials' && (
        <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 text-left">
          {/* If profile is incomplete, show a simple gate message */}
          {!isProfileComplete && (
            <div className="mb-12 px-6">
              <div className="bg-white rounded-[3rem] shadow-2xl border border-black/[0.03] p-8 md:p-10 grainy">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">📝</div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-black serif tracking-tight">
                      Finish your Venue Profile first
                    </h4>
                    <p className="mt-2 text-gray-500 font-medium leading-relaxed">
                      Add your venue name and full address (street, city, state, zip) so your listing shows up correctly in Explore.
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveTab('identity')}
                      className="mt-6 px-8 py-4 bg-black text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all shadow-xl"
                    >
                      Go to Venue Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!hasMembershipAccess && (
            <div className="mb-12 px-6">
              <div className="bg-white rounded-[3rem] shadow-2xl border border-black/[0.03] p-8 md:p-10 grainy">
                <h4 className="text-2xl font-black serif tracking-tight">Membership access is paused</h4>
                <p className="mt-2 text-gray-500 font-medium leading-relaxed">
                 Your current subscription status is <span className="font-black">{normalizedSubscriptionStatus || normalizedListingStatus || 'inactive'}</span>. Reactivate or update your billing in Stripe to keep managing specials.
                </p>
                <button
                  type="button"
                  onClick={handleManageBilling}
                  disabled={isOpeningBillingPortal}
                  className="mt-6 px-8 py-4 bg-black text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isOpeningBillingPortal ? 'Opening Billing...' : 'Reactivate Now'}
                </button>
              </div>
            </div>
          )}
      
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 px-6">
            <div>
              <h3 className="text-4xl font-black serif tracking-tight">Active Presence</h3>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">
                Manage your current happy hour offerings.
              </p>
            </div>
      
            <button
              type="button"
              onClick={() => {
                if (!isProfileComplete) {
                  setActiveTab('identity');
                  return;
                }
                if (!hasMembershipAccess) {
                  setActiveTab('identity');
                  return;
                }
                onAddNew();
              }}
              disabled={!isProfileComplete || !hasMembershipAccess}
              className={`px-8 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest flex items-center gap-4 transition-all shadow-2xl
                ${isProfileComplete && hasMembershipAccess
                  ? 'bg-black text-white hover:bg-amber-500 hover:text-black'
                  : 'bg-black/20 text-black/40 cursor-not-allowed'
                }`}
              title={!isProfileComplete ? 'Complete Venue Profile first' : !hasMembershipAccess ? 'Membership is not active' : 'List a new special'}
            >
              List New Special
            </button>
          </div>
      
          {events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {events.map(event => (
                <div key={event.id} className="flex flex-col gap-6 animate-in zoom-in-95 duration-500">
                  <SpecialCard
                    event={event}
                    hideFavorite
                    className="cursor-default hover:translate-y-0"
                  />
                  <div className="flex gap-3 px-2">
                    <button
                      type="button"
                      onClick={() => hasMembershipAccess && onEditEvent(event)}
                      disabled={!hasMembershipAccess}
                      className="flex-1 px-4 py-4 bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-all shadow-lg active:scale-95"
                    >
                      Edit Special
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        if (!hasMembershipAccess) return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (window.confirm("Are you sure? This will permanently delete this special.")) {
                          onRemoveEvent(event.id);
                        }
                      }}
                      disabled={!hasMembershipAccess}
                      className="flex-1 px-4 py-4 bg-red-50 text-red-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all border border-red-100 shadow-sm active:scale-95"
                    >
                      Remove Special
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-32 text-center border-4 border-dashed border-black/5 rounded-[4rem] grainy">
              <div className="text-5xl mb-6 grayscale opacity-20">🍺</div>
              <h3 className="text-2xl font-black serif text-gray-300">
                You haven't listed any specials yet.
              </h3>
      
              <button
                type="button"
                onClick={() => {
                  if (!isProfileComplete) {
                    setActiveTab('identity');
                    return;
                  }
                  if (!hasMembershipAccess) {
                    setActiveTab('identity');
                    return;
                  }
                  onAddNew();
                }}
                disabled={!isProfileComplete || !hasMembershipAccess}
                className={`mt-8 px-10 py-4 rounded-full font-black text-[10px] uppercase tracking-widest shadow-xl transition-all
                  ${isProfileComplete && hasMembershipAccess
                    ? 'bg-amber-500 text-black hover:bg-black hover:text-white'
                    : 'bg-black/10 text-black/30 cursor-not-allowed'
                  }`}
              >
                List My First Special
              </button>
      
              {!isProfileComplete && (
                <p className="mt-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Complete Venue Profile to unlock
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'identity' && (
        <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 space-y-20">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12 text-left">
              <h3 className="text-4xl font-black serif tracking-tight">Venue <span className="italic text-amber-600">Profile.</span></h3>
              <p className="text-gray-400 mt-2 font-medium uppercase tracking-widest text-[10px]">Customize your brand presence across the Hoppy Collective</p>
            </div>

            <form onSubmit={handleIdentitySave} className="space-y-20 text-left">
              <div className="bg-white p-8 md:p-14 rounded-[3.5rem] shadow-2xl border border-black/[0.03] grainy space-y-12">
                <div className="flex flex-col md:flex-row gap-12 items-start">
                  <div className="flex flex-col items-center">
                    <div className="w-44 h-44 rounded-[2.5rem] bg-gray-50 border-2 border-black/5 flex items-center justify-center overflow-hidden shrink-0 relative shadow-inner group">
                      {localIdentity.logoUrl ? <img src={localIdentity.logoUrl} className="w-full h-full object-contain p-6" alt="Logo" /> : <span className="text-4xl grayscale opacity-20">🎯</span>}
                      <label className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <span className="text-white font-black text-[10px] uppercase">Update Logo</span>
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'logoUrl')} />
                      </label>
                    </div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-4 text-center">Venue Logo</p>
                  </div>

                  <div className="flex-1 space-y-8 w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Venue Name</label>
                        <input
                          required
                          type="text"
                          value={localIdentity.venueName}
                          onChange={e => updateIdentityField('venueName', e.target.value)}
                          placeholder="The Rusty Anchor"
                          className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none"
                        />
                      </div>
                    
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Street Address</label>
                        <input
                          required
                          type="text"
                          value={localIdentity.streetAddress}
                          onChange={e => updateIdentityField('streetAddress', e.target.value)}
                          placeholder="123 Main St"
                          className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">City</label>
                        <input
                          required
                          type="text"
                          value={localIdentity.city}
                          onChange={e => updateIdentityField('city', e.target.value)}
                          placeholder="City"
                          className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none"
                        />
                      </div>
                    
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">State</label>
                        <input
                          required
                          type="text"
                          value={localIdentity.state}
                          onChange={e => updateIdentityField('state', e.target.value)}
                          placeholder="ST"
                          className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none"
                        />
                      </div>
                    
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Zip Code</label>
                        <input
                          required
                          type="text"
                          value={localIdentity.zipCode}
                          onChange={e => updateIdentityField('zipCode', e.target.value)}
                          placeholder="00000"
                          className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none"
                        />
                      </div>
                    </div>


                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Website URL</label>
                      <input type="text" placeholder="https://..." value={localIdentity.website} onChange={e => updateIdentityField('website', e.target.value)} className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 md:p-14 rounded-[3.5rem] shadow-2xl border border-black/[0.03] grainy space-y-8">
                <h4 className="text-[11px] font-black uppercase tracking-widest text-black border-b border-black/5 pb-4">Social Media Channels</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Instagram Handle</label>
                    <input type="text" placeholder="@yourbar" value={localIdentity.instagram} onChange={e => updateIdentityField('instagram', e.target.value)} className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold outline-none" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Facebook URL</label>
                    <input type="text" placeholder="facebook.com/yourbar" value={localIdentity.facebook} onChange={e => updateIdentityField('facebook', e.target.value)} className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-4">Twitter/X Handle</label>
                    <input type="text" placeholder="@yourbar" value={localIdentity.xHandle} onChange={e => updateIdentityField('xHandle', e.target.value)} className="w-full h-14 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-2xl px-6 font-bold text-base outline-none transition-all shadow-inner" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 md:p-14 rounded-[3.5rem] shadow-2xl border border-black/[0.03] grainy space-y-12">
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-black">Main Venue Photo (Hero)</h4>
                    <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Visible on cards & explore tab badge</span>
                  </div>
                  <div className="relative aspect-video rounded-[2.5rem] overflow-hidden bg-gray-50 border-2 border-black/5 shadow-inner group">
                    {localIdentity.mainImageUrl ? <img src={localIdentity.mainImageUrl} className="w-full h-full object-cover" alt="Main" /> : <div className="w-full h-full flex items-center justify-center opacity-10 text-6xl">🎬</div>}
                    {genLoading === 'hero' && <div className="absolute inset-0 bg-black/80 flex items-center justify-center text-[10px] text-white font-black uppercase animate-pulse tracking-widest">Auto-Generating...</div>}
                    <label className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      <span className="text-white font-black text-sm uppercase">Upload Custom Hero Image</span>
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'mainImageUrl')} />
                    </label>
                  </div>
                </div>

                <div className="space-y-8 pt-8 border-t border-black/5">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-black">Vibe Gallery (Max 4)</h4>
                    <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Atmosphere photos for your venue page</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {localIdentity.galleryImages.map((img, idx) => (
                      <div key={idx} className="relative aspect-square rounded-[2rem] overflow-hidden border-2 border-black/5 group shadow-sm">
                        <img src={img} className="w-full h-full object-cover" alt={`Vibe ${idx}`} />
                        <button type="button" onClick={() => handleRemoveGalleryImage(idx)} className="absolute top-2 right-2 w-8 h-8 bg-black/80 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 shadow-lg">✕</button>
                      </div>
                    ))}

                    {localIdentity.galleryImages.length < 4 && (
                      <label className="aspect-square rounded-[2rem] border-4 border-dashed border-black/5 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-amber-500/40 hover:bg-amber-50 transition-all group">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-amber-500/20 group-hover:text-amber-600 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                        <span className="text-[8px] font-black uppercase tracking-widest text-gray-400">Add Photo</span>
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'gallery')} />
                      </label>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSaving}
                  className={`w-full h-20 bg-black text-white rounded-[2rem] font-black text-xl uppercase tracking-[0.2em] hover:bg-amber-500 hover:text-black transition-all active:scale-[0.98] shadow-2xl flex items-center justify-center gap-4 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isSaving ? 'SAVING PROFILE...' : 'Update Profile Presence'}
                </button>
                <div className="pt-6 border-t border-black/5 space-y-4">
                  <button
                    type="button"
                    onClick={handleManageBilling}
                    disabled={isOpeningBillingPortal}
                    className="w-full h-14 bg-gray-100 text-black rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isOpeningBillingPortal ? 'Opening Billing...' : 'Manage Billing / Cancel Membership'}
                  </button>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Opens Stripe Billing Portal for plan changes and cancellation.
                  </p>
                  {billingError && <p className="text-xs font-bold text-red-500">{billingError}</p>}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'flash' && (
        <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
          <div className="max-w-4xl mx-auto text-left">
            {!hasMembershipAccess ? (
              <div className="bg-white p-12 rounded-[4rem] shadow-2xl border border-black/[0.03] grainy">
                <h3 className="text-3xl font-black serif">Flash Boost is unavailable</h3>
                <p className="text-[11px] font-bold text-gray-500 mt-4">
                  Reactivate your Stripe subscription from the billing portal to use Flash Boost again.
                </p>
              </div>
            ) : (
            <div className="bg-white p-12 rounded-[4rem] shadow-2xl border border-black/[0.03] grainy">
              <div className="flex items-center gap-6 mb-10">
                <div className="w-16 h-16 bg-amber-500 text-black rounded-3xl flex items-center justify-center text-3xl shadow-lg">⚡</div>
                <div>
                  <h3 className="text-3xl font-black serif">Flash <span className="text-amber-500">Boost.</span></h3>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Trigger instant notifications to local explorers</p>
                </div>
              </div>
              <div className="space-y-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-2">Broadcast Message</label>
                  <textarea
                    value={boostMessage}
                    onChange={(e) => setBoostMessage(e.target.value)}
                    className="w-full h-32 bg-gray-50 border-2 border-transparent focus:border-amber-500 rounded-3xl p-6 font-bold text-sm outline-none resize-none transition-all shadow-inner"
                    placeholder="e.g. BOGO Cocktails for the next hour! Mention FLASH to redeem."
                  />
                </div>
                <button
                  onClick={handleLaunchBoost}
                  disabled={isBoosting}
                  className="w-full h-20 bg-amber-500 text-black rounded-[2rem] font-black text-lg uppercase tracking-[0.2em] shadow-xl hover:bg-black hover:text-white transition-all flex items-center justify-center gap-4"
                >
                  {isBoosting ? 'BROADCASTING...' : 'LAUNCH FLASH BOOST'}
                </button>
                {boostResult && <p className="text-xs font-bold text-green-600">{boostResult}</p>}
                {boostError && <p className="text-xs font-bold text-red-500">{boostError}</p>}
              </div>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantPortal;
